import React, { useEffect, useRef, useState, Fragment } from "react";
import CssTransition from "../layout/css-transition";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import Flatpickr from "react-flatpickr";
import request from "../../service/request";
import { Listbox, Transition } from "@headlessui/react";
import moment from "moment";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import swal from "@sweetalert/with-react";
import CustomeModal from "../modal-popup/customeModal";
import { useSelector } from "react-redux";

// Adding new tooltip
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css"; // optional

const schema = yup.object({
  subject: yup
    .string()
    .required("Subject is required.")
    .min(5, "Subject must have 5 characters.")
    .max(100, "Subject must be less than 100 characters.")
    .matches(/^[^\s]+(?:$|.*[^\s]+$)/, "Blank space not allowed in subject."),
  date_time_from: yup.date().required("Date time from is required.").typeError("Date time from is required."),
  date_time_to: yup.date().typeError("Date time to is required."),
  frequency: yup.string().required("Reminder frequency is required."),
  remarks: yup
    .string()
    .required("Remark is require for reminder.")
    .min(5, "Remarks required minimum 5 characters")
    .max(500, "Remarks must be less than 500 characters")
    .matches(/^[^\s]+(?:$|.*[^\s]+$)/, "Blank space not allowed in remarks."),
});

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const AddRemainderModal = ({ id, open, setModalOpen, getResponse }) => {
  const modalContent = useRef(null);
  const dropdown = useRef(null);
  const freqDropdown = useRef(null);
  const trigger = useRef(null);
  const freqTrigger = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [freqDropdownOpen, setFreqDropdownOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedError, setSelectedError] = useState("");
  const [userList, setUserList] = useState([]);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [usersEmail, setUsersEmail] = useState([]);
  const [selectedMonthDay, setSelectedMonthDay] = useState("");
  const [yearlyMonth, setYearlyMonth] = useState("");
  const [yearlyDay, setYearlyDay] = useState("");
  const [weeklyDays, setWeeklyDays] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [day, setDay] = useState([]);
  const [characterCount, setCharacterCount] = useState(0);
  const [subjectCharacterCount, setSubjectCharacterCount] = useState(0);

  const userData = useSelector((state) => state.auth);

  const defautlValues = {
    subject: "",
    date_time_from: "",
    date_time_to: new Date(),
    frequency: "Never",
    remarks: "",
  };

  const freqList = ["Never", "Daily", "Weekly", "Monthly", "Yearly"];

  const { control, handleSubmit, reset, setValue, getValues, clearErrors, setError } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const getUser = async () => {
      const response = await request(`/user/company`, null, "GET", userData?.token);
      if (Array.isArray(response.results)) {
        setUserList(response.results);
        setUsers(response.results);
      }
    };
    getUser();
  }, []);

  // close if the esc key is pressed

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!open || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // Dropdown close on click outside
  useEffect(() => {
    const clickHandlerDropdown = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };

    const clickHandlerDropdownFreq = ({ target }) => {
      if (!freqDropdown.current) return;
      if (!freqDropdownOpen || freqDropdown.current.contains(target) || freqTrigger.current.contains(target)) return;
      setFreqDropdownOpen(false);
    };

    document.addEventListener("click", clickHandlerDropdown);
    document.addEventListener("click", clickHandlerDropdownFreq);
    return () => {
      document.removeEventListener("click", clickHandlerDropdown);
      document.removeEventListener("click", clickHandlerDropdownFreq);
    };
  });

  const handleUserSelection = (item) => {
    setSelectedError("");
    if (selected.includes(item)) {
      setSelected((prev) => prev.filter((e) => e.id !== item.id));
      return;
    }
    setSelected((prev) => [...prev, item]);
  };

  const onSubmit = async (data) => {
    if (selected.length === 0 && usersEmail.length === 0) {
      setSelectedError("Please select atleast one user for reminder.");
      return;
    }

    if (moment(data.date_time_from).isBefore(new Date(), "date")) {
      setError("date_time_from", {
        message: "Date time from must be same or future date.",
      });
      return;
    }

    if (data.frequency !== "Never" && !moment(data.date_time_to).isSameOrAfter(data.date_time_from, "date")) {
      setError("date_time_to", {
        message: "Date time to must be same or greater than date time from.",
      });
      return;
    }

    if (data.frequency !== "Never" && !moment(data.date_time_to).isAfter(data.date_time_from, "hour")) {
      setError("date_time_to", {
        message: "Date time to must be greater than date time from.",
      });
      return;
    }

    let users = [];
    for (const item of selected) {
      users.push(item.email);
    }

    const payload = {
      ...data,
      frequency: data.frequency,
      users: "",
      emails: JSON.stringify([...users, ...usersEmail]),
      company_id: userData.company.id,
      date_time_to: data.frequency === "Never" ? new Date(moment(data.date_time_from).add(1, "hour")) : data.date_time_to,
    };

    if (data.frequency === "Weekly" && weeklyDays.length === 0) {
      setErrorMessage("Please select atleast one day.");
      return;
    }

    if (data.frequency === "Monthly" && selectedMonthDay === "") {
      setErrorMessage("Please select atleast one day.");
      return;
    }

    if (data.frequency === "Yearly" && (yearlyMonth === "" || yearlyDay === "")) {
      setErrorMessage("Please select month and day.");
      return;
    }

    if (data.frequency === "Weekly") {
      payload["frequency_condition"] = JSON.stringify({
        days: weeklyDays,
      });
    }

    if (data.frequency === "Monthly") {
      payload["frequency_condition"] = JSON.stringify({
        day: selectedMonthDay,
      });
    }

    if (data.frequency === "Yearly") {
      payload["frequency_condition"] = JSON.stringify({
        day: yearlyDay,
        month: yearlyMonth,
      });
    }

    const results = await request("/reminder/", JSON.stringify(payload), "POST", userData?.token);
    if (results.status) {
      setSelectedMonthDay("");
      setYearlyMonth("");
      setYearlyDay("");
      setDay([]);
      setWeeklyDays([]);
      setErrorMessage("");
      setUsersEmail([]);
      setSelected([]);
      setModalOpen(false);
      reset(defautlValues);
      swal({
        content: (
          <CustomeModal
            type="success"
            title="Success!"
            content={`Reminder successfully added.`}
          />
        ),
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });
      if (getResponse) {
        getResponse(results.data);
      }
    }
  };

  const handleEmail = () => {
    var validRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (email.match(validRegex)) {
      if (email === "") {
        setEmailError("Email is required.");
        setDropdownOpen(true);
        return;
      }
      if (usersEmail.includes(email)) {
        setEmailError("Email already in list.");
        setDropdownOpen(true);
        return;
      }
      setUsersEmail((prev) => [...prev, email]);
      setEmail("");
    } else {
      setDropdownOpen(true);
      setEmailError("Please enter valid email.");
    }
  };

  const handleEmailRemove = (email) => {
    setUsersEmail((prev) => prev.filter((item) => item !== email));
  };

  const handleUserRemove = (item) => {
    setSelected((prev) => prev.filter((e) => e.id !== item.id));
  };

  const handleWeeklyDays = (item) => {
    setErrorMessage("");
    if (weeklyDays.includes(item)) {
      setWeeklyDays((prev) => prev.filter((ele) => ele !== item));
      return;
    }
    setWeeklyDays((prev) => [...prev, item]);
  };

  const setMonthDaysList = (month) => {
    const count = moment(month, "MMMM").daysInMonth();
    let days = [];
    for (let i = 1; i <= count; i++) {
      days.push(i);
    }
    setDay(days);
  };

  const handleUserSearch = (data) => {
    const search = users.filter((item) => item.name.toLowerCase().includes(data.toLowerCase()) || item.email.toLowerCase().includes(data.toLowerCase()));
    setUserList(search);
  };

  return (
    <>
      {/* Modal backdrop */}
      <CssTransition className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity" show={open} enter="transition ease-out duration-200" enterStart="opacity-0" enterEnd="opacity-100" leave="transition ease-out duration-100" leaveStart="opacity-100" leaveEnd="opacity-0" aria-hidden="true" />
      {/* Modal dialog */}
      <CssTransition id={id} className="fixed inset-0 z-50 overflow-hidden flex items-start top-20 mb-4 justify-center px-4 sm:px-6" role="dialog" aria-modal="true" show={open} enter="transition ease-in-out duration-200" enterStart="opacity-0 translate-y-4" enterEnd="opacity-100 translate-y-0" leave="transition ease-in-out duration-200" leaveStart="opacity-100 translate-y-0" leaveEnd="opacity-0 translate-y-4">
        <div ref={modalContent} className="bg-white overflow-auto max-w-lg w-full max-h-full rounded shadow-lg p-5">
          <div className="text-lg font-medium text-gray-900 font-inter text-center py-2">Set reminder</div>
          <form className="mt-5" onSubmit={handleSubmit(onSubmit)}>
            {/* Subject */}
            <Controller
              name="subject"
              control={control}
              defaultValue={defautlValues.subject}
              render={({ field: { onChange, ...field }, formState }) => (
                <div className="flex flex-col mb-4 relative">
                  <label htmlFor="subject" className="font-inter font-medium text-sm leading-5 text-gray-700 mb-1">
                    Subject<sup className="text-rose-500 text-xs">*</sup>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-1 flex items-center pl-2 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                        <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                      </svg>
                    </div>
                    <input
                      {...field}
                      onChange={(e) => {
                        onChange(e);
                        setSubjectCharacterCount(e.target.value.split("").length);
                      }}
                      type="text"
                      className="form-input pl-10 w-full disabled:border-slate-200"
                    />
                    <div className="text-[10px] text-gray-400 mt-1 absolute right-0 -bottom-4">{subjectCharacterCount}/100 Characters</div>
                  </div>
                  <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{formState.errors?.subject?.message}</div>
                </div>
              )}
            />

            {/* Remarks */}
            <Controller
              name="remarks"
              control={control}
              defaultValue={defautlValues.remarks}
              render={({ field: { onChange, ...field }, formState }) => (
                <div className="flex flex-col mb-4 relative">
                  <label htmlFor="subject" className="font-inter font-medium text-sm leading-5 text-gray-700 mb-1">
                    Remarks<sup className="text-rose-500 text-xs">*</sup>
                  </label>
                  <textarea
                    {...field}
                    onChange={(e) => {
                      onChange(e);
                      setCharacterCount(e.target.value.split("").length);
                    }}
                    type="text"
                    className="form-input p-2 w-full disabled:border-slate-200 resize-none"
                  />
                  <div className="text-[10px] text-gray-400 mt-1 absolute right-0 -bottom-3">{characterCount}/500 Characters</div>
                  <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{formState.errors?.remarks?.message}</div>
                </div>
              )}
            />

            {/* Select Users */}
            <div className="flex flex-col mb-4">
              <label htmlFor="users" className="font-inter font-medium text-sm leading-5 text-gray-700 mb-1">
                Select Users<sup className="text-rose-500 text-xs">*</sup>
              </label>
              <div className="relative inline-flex min-w-[170px]">
                <button
                  ref={trigger}
                  type="button"
                  aria-label="Select Users"
                  aria-haspopup="true"
                  onClick={() => {
                    setDropdownOpen(!dropdownOpen);
                  }}
                  aria-expanded={dropdownOpen}
                  className="btn justify-between w-full bg-white border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-600"
                >
                  <span className="flex items-center">
                    <span>Please select users</span>
                  </span>
                  <svg className="shrink-0 ml-1 fill-current text-slate-400" width="11" height="7" viewBox="0 0 11 7">
                    <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
                  </svg>
                </button>
                <CssTransition show={dropdownOpen} tag="div" className="z-60 overflow-y-auto absolute min-h-min max-h-48 top-full left-0 w-full bg-white border border-slate-200 py-1.5 rounded shadow-lg overflow-hidden mt-1" enter="transition ease-out duration-100 transform" enterStart="opacity-0 -translate-y-2" enterEnd="opacity-100 translate-y-0" leave="transition ease-out duration-100" leaveStart="opacity-100" leaveEnd="opacity-0">
                  <div ref={dropdown} className="font-medium text-sm text-slate-600" onFocus={() => setDropdownOpen(true)} onBlur={() => setDropdownOpen(false)}>
                    <div className="p-2">
                      <div className="relative">
                        <div
                          className="absolute inset-y-0 right-1 flex items-center pr-2 text-green-400 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmail();
                          }}
                          onKeyPress={(e) => {
                            e.key === "Enter" && e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          value={email}
                          onChange={(e) => {
                            setEmailError("");
                            setEmail(e.target.value);
                            handleUserSearch(e.target.value);
                          }}
                          className="form-input pl-2 w-full disabled:border-slate-200"
                          style={{ paddingRight: 40 }}
                        />
                      </div>
                      <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{emailError ? emailError : ""}</div>
                    </div>
                    {userList.map((item) => (
                      <div
                        key={item.id}
                        tabIndex="0"
                        className={`flex items-center flex-nowrap w-full hover:bg-slate-50 py-1 px-3 cursor-pointer ${item.id === selected && "text-[--bg-color-500]"}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleUserSelection(item);
                        }}
                      >
                        <svg className={`shrink-0 mr-2 fill-current text-[--bg-color-500] ${!selected.includes(item) && "invisible"}`} width="12" height="9" viewBox="0 0 12 9">
                          <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                        </svg>
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </CssTransition>
              </div>
              {selectedError !== "" && <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{selectedError}</div>}
              <div className="w-full flex flex-row flex-wrap mt-3">
                {selected.length !== 0 &&
                  selected.map((ele, index) => (
                    <p key={index} className="flex flex-row items-center text-xs font-normal text-white bg-green-600 rounded-xl py-1 px-2 mx-1 mb-1">
                      {ele.name}
                      <span className="pl-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4 text-rose-700 cursor-pointer hover:bg-gray-400 hover:rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserRemove(ele);
                          }}
                        >
                          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                      </span>
                    </p>
                  ))}
                {usersEmail.length !== 0 &&
                  usersEmail.map((ele, index) => (
                    <p key={index} className="flex flex-row items-center text-xs font-normal text-white bg-green-600 rounded-xl py-1 px-2 mx-1 mb-1">
                      {ele}
                      <span className="pl-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4 text-rose-700 cursor-pointer hover:bg-gray-400 hover:rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmailRemove(ele);
                          }}
                        >
                          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                      </span>
                    </p>
                  ))}
              </div>
            </div>

            {/* Reminder Frequency */}
            <Controller
              name="frequency"
              control={control}
              defaultValue={defautlValues.frequency}
              render={({ field, formState }) => (
                <div className="flex flex-col mb-4">
                  <label for="frequency" class="block text-sm font-medium text-gray-700">
                    Recurring<sup className="text-rose-500 text-xs">*</sup>
                  </label>
                  <div class="flex">
                    <span class="inline-flex items-center px-3 text-sm text-gray-500 bg-gray-100 rounded-l-md border border-r-0 border-gray-300">Every</span>
                    {/* Frequency Dropdown */}
                    <div className="relative inline-flex min-w-[170px] w-full -ml-1">
                      <button
                        ref={freqTrigger}
                        type="button"
                        aria-label="Select Recurring"
                        aria-haspopup="true"
                        onClick={() => {
                          setFreqDropdownOpen(!freqDropdownOpen);
                        }}
                        aria-expanded={freqDropdownOpen}
                        className="btn justify-between w-full bg-white border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-600"
                      >
                        <span className="flex items-center">
                          <span> {getValues("frequency") ? getValues("frequency") : "Select reminder recurring"}</span>
                        </span>
                        <svg className="shrink-0 ml-1 fill-current text-slate-400" width="11" height="7" viewBox="0 0 11 7">
                          <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
                        </svg>
                      </button>
                      <CssTransition show={freqDropdownOpen} tag="div" className="z-11 overflow-y-auto absolute min-h-min max-h-36 bottom-full left-0 w-full bg-white border border-slate-200 py-1.5 rounded shadow-lg overflow-hidden mt-1" enter="transition ease-out duration-100 transform" enterStart="opacity-0 -translate-y-2" enterEnd="opacity-100 translate-y-0" leave="transition ease-out duration-100" leaveStart="opacity-100" leaveEnd="opacity-0">
                        <div ref={freqDropdown} className="font-medium text-sm text-slate-600" onFocus={() => setFreqDropdownOpen(true)} onBlur={() => setFreqDropdownOpen(false)}>
                          {freqList.map((item, index) => (
                            <div
                              key={index}
                              tabIndex="0"
                              className={`flex items-center flex-nowrap w-full hover:bg-slate-50 py-1 px-3 cursor-pointer ${item.id === selected && "text-[--bg-color-500]"}`}
                              onClick={(e) => {
                                e.preventDefault();
                                setValue("frequency", item);
                                clearErrors("frequency");
                                setFreqDropdownOpen(false);
                                setSelectedMonthDay("");
                                setYearlyMonth("");
                                setYearlyDay("");
                                setDay([]);
                                setWeeklyDays([]);
                                setErrorMessage("");
                                if (item === "Monthly") {
                                  setMonthDaysList(moment().format("MMMM"));
                                }
                              }}
                            >
                              <svg className={`shrink-0 mr-2 fill-current text-[--bg-color-500] ${getValues("frequency") !== item && "invisible"}`} width="12" height="9" viewBox="0 0 12 9">
                                <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                              </svg>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </CssTransition>
                    </div>
                  </div>
                  <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{formState.errors?.frequency?.message}</div>
                </div>
              )}
            />

            {/* Frequency Control Options */}

            {/* Week list */}
            {getValues("frequency") === "Weekly" && (
              <div>
                <div className="flex flex-row flex-wrap items-center">
                  {days.map((item, index) => (
                    <div className="mr-2 mb-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox"
                          onChange={() => {
                            handleWeeklyDays(item);
                          }}
                        />
                        <span className="text-sm ml-2">{item}</span>
                      </label>
                    </div>
                  ))}
                </div>
                {errorMessage ? <div className="text-xs text-rose-500 mt-1">{errorMessage}</div> : ""}
              </div>
            )}

            {/* Monthly List */}
            {getValues("frequency") === "Monthly" && (
              <div>
                <div className="flex flex-row items-center">
                  <div className="font-inter text-sm pr-3">Select Day</div>
                  <div className="w-40 z-50">
                    <Listbox
                      value={selectedMonthDay}
                      onChange={(e) => {
                        setSelectedMonthDay(e);
                        setErrorMessage("");
                      }}
                    >
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full form-input cursor-default rounded-lg bg-white py-1 pl-3 pr-10 text-left focus:outline-none focus-visible:border-[--bg-color-500] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                          <span className="block truncate">{selectedMonthDay ? selectedMonthDay : "Please select day"}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute bottom-full mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {day.map((item, index) => (
                              <Listbox.Option key={index} className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-amber-100 text-amber-900" : "text-gray-900"}`} value={item}>
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{item}</span>
                                    {selectedMonthDay === item ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                </div>
                {errorMessage ? <div className="text-xs text-rose-500 mt-1">{errorMessage}</div> : ""}
              </div>
            )}

            {/* Yearly List */}
            {getValues("frequency") === "Yearly" && (
              <div>
                <div className="flex flex-row items-center space-x-2">
                  <div className="font-inter text-sm">Month</div>
                  <div className="w-40 z-50">
                    <Listbox
                      value={yearlyMonth}
                      onChange={(e) => {
                        setDay([]);
                        setYearlyMonth(e);
                        setMonthDaysList(e);
                        setErrorMessage("");
                      }}
                    >
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full form-input cursor-default rounded-lg bg-white py-1 pl-3 pr-10 text-left focus:outline-none focus-visible:border-[--bg-color-500] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                          <span className="block truncate">{yearlyMonth ? yearlyMonth : "Select Month"}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute bottom-full mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {months.map((item, index) => (
                              <Listbox.Option key={index} className={({ active }) => `relative cursor-default select-none py-2 pl-8 pr-4 ${active ? "bg-amber-100 text-amber-900" : "text-gray-900"}`} value={item}>
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{item}</span>
                                    {yearlyMonth === item ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-1 text-amber-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                  <div className="font-inter text-sm">Day</div>
                  <div className="w-28 z-50">
                    <Listbox
                      value={yearlyDay}
                      onChange={(e) => {
                        setYearlyDay(e);
                        setErrorMessage("");
                      }}
                    >
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full form-input cursor-default rounded-lg bg-white py-1 pl-3 pr-10 text-left focus:outline-none focus-visible:border-[--bg-color-500] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                          <span className="block truncate">{yearlyDay ? yearlyDay : "Select day"}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute bottom-full mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {day.map((item, index) => (
                              <Listbox.Option key={index} className={({ active }) => `relative cursor-default select-none py-2 pl-8 pr-4 ${active ? "bg-amber-100 text-amber-900" : "text-gray-900"}`} value={item}>
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{item}</span>
                                    {yearlyDay === item ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-1 text-amber-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                </div>
                {errorMessage ? <div className="text-xs text-rose-500 mt-1">{errorMessage}</div> : ""}
              </div>
            )}

            {/* Time Duration */}
            <div className="flex justify-end items-center px-1">
              <Tippy content="The input time is based on your nation’s time zone.">
                <span className="fas fa-info-circle fill-current text-[--bg-color-500] cursor-pointer"></span>
              </Tippy>
            </div>
            <div className="grid grid-cols-12 mb-4 gap-x-6">
              <div className="col-span-6">
                <Controller
                  name="date_time_from"
                  control={control}
                  defaultValue={defautlValues.date_time_from}
                  render={({ field, formState }) => (
                    <div className="flex flex-col">
                      <label htmlFor="date_time_from" className="font-inter font-medium text-sm leading-5 text-gray-700 mb-1">
                        Date Time From
                        <sup className="text-rose-500 text-xs">*</sup>
                      </label>
                      <Flatpickr {...field} data-enable-time className="form-input w-full disabled:border-slate-200 cursor-pointer" />
                      <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{formState.errors?.date_time_from?.message}</div>
                    </div>
                  )}
                />
              </div>

              {getValues("frequency") !== "Never" && (
                <div className="col-span-6">
                  <Controller
                    name="date_time_to"
                    control={control}
                    defaultValue={defautlValues.date_time_to}
                    render={({ field, formState }) => (
                      <div className="flex flex-col">
                        <label htmlFor="date_time_to" className="font-inter font-medium text-sm leading-5 text-gray-700 mb-1">
                          Date Time To
                          <sup className="text-rose-500 text-xs">*</sup>
                        </label>
                        <Flatpickr {...field} data-enable-time className="form-input w-full disabled:border-slate-200 cursor-pointer" />
                        <div className="text-rose-500 font-inter font-extralight text-xs pt-1">{formState.errors?.date_time_to?.message}</div>
                      </div>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Button Groups */}
            <div className="flex justify-space space-x-2 m-1.5 mt-6">
              <button
                type="button"
                onClick={() => {
                  setSelectedMonthDay("");
                  setYearlyMonth("");
                  setYearlyDay("");
                  setDay([]);
                  setWeeklyDays([]);
                  setErrorMessage("");
                  setUsersEmail([]);
                  setSelected([]);
                  setModalOpen(false);
                  reset(defautlValues);
                }}
                className="btn-sm w-full h-10 border-slate-200 hover:border-slate-300 text-slate-600"
              >
                Cancel
              </button>
              <button type="submit" className="btn-sm w-full h-10 bg-[--bg-color-500] hover:bg-[--bg-color-600] text-white">
                Set
              </button>
            </div>
          </form>
        </div>
      </CssTransition>
    </>
  );
};

export default AddRemainderModal;
